import React, { useEffect, useMemo, useState } from "react";
import { api, brl } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Smartphone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import CurrencyInput from "@/components/CurrencyInput";

const REPAIR_FIELDS = [
  ["repair_board", "Reparo de Placa"],
  ["repair_screen", "Troca de Tela"],
  ["repair_battery", "Troca de Bateria"],
  ["repair_back", "Troca de Tampa Traseira"],
  ["repair_chassis", "Troca de Carcaça"],
  ["repair_dock", "Troca de Conector de Carga"],
  ["repair_lens", "Troca de Lente da Câmera"],
  ["repair_back_cam", "Troca de Câmera Traseira"],
  ["repair_front_cam", "Troca de Câmera Frontal"],
  ["repair_speaker", "Troca de Alto-Falante"],
  ["repair_faceid", "Reparo Face ID"],
  ["repair_button", "Troca de Botão"],
];

const BLANK_MODEL = {
  name: "", photo: "",
  price_perfect: 0, price_good: 0, price_regular: 0, price_bad: 0,
  repair_board: 0, repair_screen: 0, repair_battery: 0, repair_back: 0,
  repair_chassis: 0, repair_dock: 0, repair_lens: 0, repair_back_cam: 0,
  repair_front_cam: 0, repair_speaker: 0, repair_faceid: 0, repair_button: 0,
  custom_repairs: [],
};

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function MasterEvaluator() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK_MODEL);
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/master/evaluator-models");
    setList(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!q) return list;
    return list.filter(m => m.name.toLowerCase().includes(q.toLowerCase()));
  }, [list, q]);

  const seedDefault = async () => {
    if (!window.confirm("Cadastrar os 32 modelos padrão de iPhone (XR → 17 Pro Max)? Modelos existentes não serão duplicados.")) return;
    setBusy(true);
    try {
      const r = await api.post("/master/evaluator-models/seed");
      toast.success(`${r.data.inserted} modelo(s) adicionado(s)`);
      load();
    } catch { toast.error("Erro"); }
    setBusy(false);
  };

  const openNew = () => { setForm(BLANK_MODEL); setEditingId(null); setOpen(true); };
  const openEdit = (m) => {
    setForm({ ...BLANK_MODEL, ...m });
    setEditingId(m.id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name?.trim()) return toast.error("Nome do modelo é obrigatório");
    setBusy(true);
    try {
      if (editingId) await api.put(`/master/evaluator-models/${editingId}`, form);
      else await api.post("/master/evaluator-models", form);
      toast.success("Salvo");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
    setBusy(false);
  };

  const remove = async (m) => {
    if (!window.confirm(`Excluir "${m.name}"?`)) return;
    await api.delete(`/master/evaluator-models/${m.id}`);
    toast.success("Excluído"); load();
  };

  const duplicate = async (m) => {
    await api.post(`/master/evaluator-models/${m.id}/duplicate`);
    toast.success("Duplicado"); load();
  };

  const onPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.5 * 1024 * 1024) return toast.error("Foto até 1.5MB");
    const b64 = await fileToBase64(file);
    setForm(f => ({ ...f, photo: b64 }));
  };

  const setCustomRepair = (idx, key, val) => {
    setForm(f => ({
      ...f,
      custom_repairs: f.custom_repairs.map((r, i) => i === idx ? { ...r, [key]: val } : r)
    }));
  };

  const addCustomRepair = () => {
    setForm(f => ({ ...f, custom_repairs: [...f.custom_repairs, { name: "", price: 0 }] }));
  };
  const delCustomRepair = (idx) => {
    setForm(f => ({ ...f, custom_repairs: f.custom_repairs.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6" data-testid="master-evaluator-page">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <p className="label-tiny mb-2 inline-flex items-center gap-1"><Smartphone size={11}/> Avaliador de iPhone</p>
          <h1 className="font-display text-4xl tracking-tight">Modelos de iPhone</h1>
          <p className="text-sm text-neutral-500 mt-1">Cadastre valores por estado e custos de reparo. {list.length} modelo(s).</p>
        </div>
        <div className="flex gap-2">
          {list.length === 0 && (
            <Button variant="outline" onClick={seedDefault} disabled={busy} data-testid="seed-models-btn">
              <Sparkles size={14} className="mr-1"/> Cadastrar 32 modelos padrão
            </Button>
          )}
          <Button onClick={openNew} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="new-model-btn">
            <Plus size={16} className="mr-1"/> Novo modelo
          </Button>
        </div>
      </div>

      <Input placeholder="Buscar modelo..." value={q} onChange={e=>setQ(e.target.value)} className="max-w-sm"/>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(m => (
          <Card key={m.id} className="p-3 border-[#E5E5E5] shadow-none" data-testid={`model-${m.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-16 h-16 rounded-md bg-neutral-100 flex-shrink-0 flex items-center justify-center overflow-hidden">
                {m.photo
                  ? <img src={m.photo} alt={m.name} className="w-full h-full object-cover"/>
                  : <Smartphone size={20} className="text-neutral-400"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{m.name}</div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  Perfeito {brl(m.price_perfect)} · Bom {brl(m.price_good)}
                </div>
                <div className="text-[11px] text-neutral-500">
                  Regular {brl(m.price_regular)} · Ruim {brl(m.price_bad)}
                </div>
                <div className="flex items-center gap-0.5 mt-2">
                  <Button size="sm" variant="ghost" onClick={()=>openEdit(m)} title="Editar" data-testid={`edit-model-${m.id}`}><Pencil size={12}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>duplicate(m)} title="Duplicar" data-testid={`dup-model-${m.id}`}><Copy size={12}/></Button>
                  <Button size="sm" variant="ghost" onClick={()=>remove(m)} title="Excluir" data-testid={`del-model-${m.id}`}><Trash2 size={12}/></Button>
                </div>
              </div>
            </div>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-16 text-sm text-neutral-500">Nenhum modelo. Clique em "Cadastrar 32 modelos padrão" pra começar rápido.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editingId ? "Editar modelo" : "Novo modelo"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Modelo</Label><Input value={form.name} onChange={e=>setForm({...form, name: e.target.value})} placeholder="Ex.: iPhone 15 Pro Max" data-testid="model-name-input"/></div>
            <div>
              <Label>Foto do aparelho</Label>
              <div className="flex items-center gap-3 mt-1">
                {form.photo && <img src={form.photo} alt="preview" className="w-20 h-20 rounded-md object-cover"/>}
                <Input type="file" accept="image/*" onChange={onPhotoUpload} className="text-xs" data-testid="model-photo-input"/>
                {form.photo && <Button size="sm" variant="outline" onClick={()=>setForm({...form, photo:""})}>Remover</Button>}
              </div>
            </div>

            <Card className="p-3 bg-neutral-50 border-[#E5E5E5] shadow-none">
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">Valores por estado</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ["price_perfect","Perfeito"],
                  ["price_good","Bom"],
                  ["price_regular","Regular"],
                  ["price_bad","Ruim"],
                ].map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-[10px] text-neutral-500">{label}</Label>
                    <CurrencyInput value={form[k]} onChange={(v)=>setForm({...form, [k]: v})} data-testid={`field-${k}`}/>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-3 bg-neutral-50 border-[#E5E5E5] shadow-none">
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-600 mb-2">Custos de reparo</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {REPAIR_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <Label className="text-[10px] text-neutral-500">{label}</Label>
                    <CurrencyInput value={form[k]} onChange={(v)=>setForm({...form, [k]: v})} data-testid={`field-${k}`}/>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-3 bg-neutral-50 border-[#E5E5E5] shadow-none">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-neutral-600">Reparos personalizados</div>
                <Button size="sm" variant="outline" onClick={addCustomRepair} data-testid="add-custom-repair"><Plus size={12} className="mr-1"/>Adicionar</Button>
              </div>
              {(form.custom_repairs || []).map((r, idx) => (
                <div key={idx} className="flex items-end gap-2 mb-2">
                  <div className="flex-1"><Label className="text-[10px]">Nome</Label><Input value={r.name} onChange={e=>setCustomRepair(idx, "name", e.target.value)} placeholder="Ex.: Limpeza interna"/></div>
                  <div className="w-32"><Label className="text-[10px]">Valor</Label><CurrencyInput value={r.price} onChange={(v)=>setCustomRepair(idx, "price", v)}/></div>
                  <Button size="sm" variant="ghost" onClick={()=>delCustomRepair(idx)}><Trash2 size={12}/></Button>
                </div>
              ))}
              {(form.custom_repairs || []).length === 0 && <p className="text-[11px] text-neutral-400 italic">Nenhum reparo extra.</p>}
            </Card>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] hover:bg-black text-white" data-testid="save-model-btn">
              {busy ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
